import { FlatList, RefreshControl, ScrollView, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import CommonHeader from '../../../shared/commonComponents/commonHeader'
import { appColors, appFonts, fontSize } from '../../../themes/appTheme'
import { ZenBadge, ZenEmptyComponent, ZenLoader, ZenDateTimePicker, ZenToast } from 'zen-core-elements'
import { useIsFocused } from '@react-navigation/native'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import CommonService from '../../../shared/services/commonService'
import { useAppNavigation, useAppRoute } from '../../../types/@react-navigation/navigation'
import ZenIcon from 'zen-core-elements/src/elements/zenIcon/zenIcon'
import { RevenueData } from './revenueReport.module'
import ZenText from 'zen-core-elements/src/elements/zenText/zenText'
import { getAllRevenueReport } from '../../../redux/slices/revenueReport.slice'
import useBackHandler from '../../../shared/hooks/useBackHandler'
import moment from 'moment'
import SellscreenModal from '../../newSellscreen/sellscreenModal'
import { showApiError } from '../../../shared/utils/handleApiError';

const limit = 10

interface DetailTileProps {
    label: string;
    value: number | undefined;
    icon: string;
    color: string;
    currencySymbol: string;
    format: (val: number | undefined) => string;
    isLandscape: boolean;
}

const DetailTile = ({ label, value, icon, color, currencySymbol, format, isLandscape }: DetailTileProps) => (
    <View style={[styles.tile, !isLandscape && styles.tilePortrait]}>
        {isLandscape ? (
            <>
                <ZenIcon type='material-community' name={icon} size={28} color={color} />
                <ZenText style={styles.tileLabel} content={label} color={appColors.lightDark} size={fontSize.smallHeaderFontSize} />
                <ZenText style={styles.tileValue} bold content={CommonService.currencyStandard(currencySymbol, String(value ?? 0))} color={appColors.dark} size={fontSize.largerFontSize} />
            </>
        ) : (
            <View style={styles.tilePortraitContent}>
                <View style={[styles.iconContainerPortrait, { backgroundColor: color + '15' }]}>
                    <ZenIcon type='material-community' name={icon} size={24} color={color} />
                </View>
                <View style={styles.tileTextContainer}>
                    <ZenText content={label} color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.tileLabelPortrait} />
                    <ZenText bold content={CommonService.currencyStandard(currencySymbol, String(value ?? 0))} color={appColors.dark} size={fontSize.mediumFontSize} />
                </View>
            </View>
        )}
    </View>
);

interface InfoItemProps {
    label: string;
    value: number | undefined;
    color?: string;
    currencySymbol: string;
    format: (val: number | undefined) => string;
}

const InfoItem = ({ label, value, color = appColors.dark, currencySymbol, format }: InfoItemProps) => (
    <View style={styles.infoItem}>
        <ZenText style={styles.infoLabel} content={label} color={appColors.draggerIcon} size={fontSize.errorFontSize} />
        <ZenText style={{ color }} content={CommonService.currencyStandard(currencySymbol, String(value ?? 0))} size={fontSize.smallFontSize} bold />
    </View>
);

const InfoItemRow = ({ label, value, color = appColors.dark, currencySymbol, format }: InfoItemProps) => (
    <View style={styles.infoItemRow}>
        <ZenText style={styles.infoLabelRow} content={label} color={appColors.draggerIcon} size={fontSize.errorFontSize} />
        <ZenText style={{ color }} content={CommonService.currencyStandard(currencySymbol, String(value ?? 0))} size={fontSize.smallFontSize} bold />
    </View>
);

const RenderDetailedView = ({ selectedItem, format, currencySymbol }: { selectedItem: RevenueData | null, format: (val: number | undefined) => string, currencySymbol: string }) => {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;

    return (
        <ScrollView style={styles.mainContent} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 20 }}>
            <View style={styles.mainHeader}>
                <ZenText content="Transaction Overview" color={appColors.primary} bold size={fontSize.largerFontSize} />
                <ZenText content={`Date: ${selectedItem?.orderDate}`} color={appColors.lightDark} size={fontSize.smallHeaderFontSize} />
            </View>

            <View style={styles.detailGrid}>
                {/* Top Primary Row */}
                <DetailTile label="Net Sale" value={selectedItem?.netSale} icon="wallet" color={appColors.blue600} currencySymbol={currencySymbol!} format={format} isLandscape={isLandscape} />
                <DetailTile label="Amount Paid" value={selectedItem?.paid} icon="check-decagram" color={appColors.tealGreen} currencySymbol={currencySymbol!} format={format} isLandscape={isLandscape} />
                <DetailTile label="Remaining" value={selectedItem?.toPay} icon="clock-fast" color={appColors.goldenOrange} currencySymbol={currencySymbol!} format={format} isLandscape={isLandscape} />

                {/* secondary info block */}
                <View style={styles.infoBlock}>
                    <ZenText style={styles.blockTitle} content="Revenue Breakdown" color={appColors.primary} bold size={fontSize.largerFontSize} />
                    {isLandscape ? (
                        <>
                            <View style={styles.row}>
                                <InfoItem label="Total Sales" value={selectedItem?.totalSales} currencySymbol={currencySymbol} format={format} />
                                <InfoItem label="Tax (GST/VAT)" value={selectedItem?.tax} currencySymbol={currencySymbol} format={format} />
                                <InfoItem label="Extra Amount" value={selectedItem?.extraAmount} currencySymbol={currencySymbol} format={format} />
                            </View>
                            <View style={styles.row}>
                                <InfoItem label="Refunds" value={selectedItem?.refund} color={appColors.red500} currencySymbol={currencySymbol} format={format} />
                                <InfoItem label="Discounts" value={selectedItem?.discount} color={appColors.blue600} currencySymbol={currencySymbol} format={format} />
                                <InfoItem label="Gift Cards" value={selectedItem?.giftCardAmount} currencySymbol={currencySymbol} format={format} />
                            </View>
                        </>
                    ) : (
                        <View style={styles.portraitList}>
                            <InfoItemRow label="Total Sales" value={selectedItem?.totalSales} currencySymbol={currencySymbol} format={format} />
                            <InfoItemRow label="Tax (GST/VAT)" value={selectedItem?.tax} currencySymbol={currencySymbol} format={format} />
                            <InfoItemRow label="Extra Amount" value={selectedItem?.extraAmount} currencySymbol={currencySymbol} format={format} />
                            <InfoItemRow label="Refunds" value={selectedItem?.refund} color={appColors.red500} currencySymbol={currencySymbol} format={format} />
                            <InfoItemRow label="Discounts" value={selectedItem?.discount} color={appColors.blue600} currencySymbol={currencySymbol} format={format} />
                            <InfoItemRow label="Gift Cards" value={selectedItem?.giftCardAmount} currencySymbol={currencySymbol} format={format} />
                        </View>
                    )}
                </View>

                <View style={styles.infoBlock}>
                    <ZenText style={styles.blockTitle} content="Auditing & Reconciliation" color={appColors.primary} bold size={fontSize.largerFontSize} />
                    {isLandscape ? (
                        <View style={styles.row}>
                            <InfoItem label="Gross (Before Refund)" value={(selectedItem?.grossSaleBeforeRefunds)} currencySymbol={currencySymbol} format={format} />
                            <InfoItem label="Gross (After Refund)" value={(selectedItem?.grossSaleAfterRefunds)} currencySymbol={currencySymbol} format={format} />
                        </View>
                    ) : (
                        <View style={styles.portraitList}>
                            <InfoItemRow label="Gross (Before Refund)" value={(selectedItem?.grossSaleBeforeRefunds)} currencySymbol={currencySymbol} format={format} />
                            <InfoItemRow label="Gross (After Refund)" value={(selectedItem?.grossSaleAfterRefunds)} currencySymbol={currencySymbol} format={format} />
                        </View>
                    )}
                </View>
            </View>
        </ScrollView>
    );
};

let offset = 0;

const RevenueReport = () => {
    useBackHandler()
    const isFocused = useIsFocused()
    const { loading, error, value, isReached, filter } = useAppSelector((state) => state.revenueReport.getAllRevenueReport);
    const revenueReportData = value?.revenueReport;
    const route = useAppRoute("RevenueReport")
    const navigation = useAppNavigation()
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const { id: locationId } = useAppSelector(state => state?.common?.currentLocation)

    //variable for handle refresh control
    const [listRefresh, setListRefresh] = useState(false)

    const { width, height } = useWindowDimensions()
    const dispatch = useAppDispatch()

    const [modalVisible, setModalVisible] = useState(false)
    //Variable to handle open state of the date picker component
    const [datePickerVisibile, setDatePickerVisible] = useState<{
        visibilty: boolean,
        current: 'from' | 'to' | null
    }>({
        visibilty: false, current: null
    })

    const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
        from: route?.params?.beginDate!,
        to: route?.params?.endDate!,
    });

    useEffect(() => {
        if (isFocused) {
            offset = 0;
            setSelectedItem(null);
            getAllRevenueReportData(listRefresh ? 0 : offset, listRefresh);
        }
    }, [isFocused, dateRange])

    const getAllRevenueReportData = async (currentOffset?: number, forceRefresh = false, range = dateRange) => {
        const payload = {
            limit: limit,
            offset: currentOffset !== undefined ? currentOffset : 0,
            filterData: JSON.stringify({ customDateRange: { begin: range?.from, end: range?.to } }),
            locationId: locationId
        }
        if (!forceRefresh && payload.offset === 0 && filter === payload.filterData && value) return
        dispatch(getAllRevenueReport({ data: payload })).unwrap().catch(showApiError).finally(() => {
            setListRefresh(false)
        })
    }
    if (error) {
        ZenToast({ type: 'error', message: error });
    }

    const summaryItem: RevenueData = useMemo(() => {
        return {
            ...value?.revenueReport?.revenueDetails!,
            orderDate: 'All Time',
            orderCount: value?.revenueReport?.count!,
            paid: value?.revenueReport?.revenueDetails?.recivedAmount!,
            toPay: value?.revenueReport?.revenueDetails?.pendingAmount!,
        }
    }, [value])
    const isLandscape = useMemo(() => width > height, [width, height])
    const [selectedItem, setSelectedItem] = useState<RevenueData | null>(null);

    const activeItem = useMemo(() => {
        if (selectedItem) return selectedItem;
        if (isLandscape && (revenueReportData?.revenueData?.length ?? 0) > 0) {
            return summaryItem;
        }
        return null;
    }, [selectedItem, isLandscape, revenueReportData, summaryItem]);

    const format = (val: number | undefined) => (val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0 });

    const prevIsLandscape = useRef<boolean>(isLandscape);

    useEffect(() => {
        if (prevIsLandscape.current === true && !isLandscape) {
            if (activeItem) {
                setModalVisible(true);
            }
        } else if (prevIsLandscape.current === false && isLandscape) {
            setModalVisible(false);
        }
        prevIsLandscape.current = isLandscape;
    }, [isLandscape, activeItem]);

    const onRequestClose = () => {
        setModalVisible(false)
        setSelectedItem(null)
    }

    const onRefresh = () => {
        setListRefresh(true);
        offset = 0;
        const resetRange = { from: route?.params?.beginDate!, to: route?.params?.endDate! };
        setDateRange(resetRange);
        setSelectedItem(null);
        getAllRevenueReportData(0, true, resetRange);
    };

    const renderSummaryCard = () => {
        const isSelected = isLandscape
            ? activeItem?.orderDate === 'All Time'
            : selectedItem?.orderDate === 'All Time';
        return (
            <TouchableOpacity
                onPress={() => {
                    if (!isLandscape) {
                        setModalVisible(true)
                        setSelectedItem(summaryItem)
                    } else {
                        setSelectedItem(null)
                    }
                }}
                style={[styles.orderCard, isSelected && styles.selectedCard, { marginBottom: 20, borderColor: isSelected ? appColors.blue600 : 'transparent', borderWidth: 1, marginHorizontal: 15 }]}
            >
                <View style={styles.cardHeader}>
                    <ZenText color={appColors.dark} bold content="All Time Overview" size={fontSize.smallHeaderFontSize} />

                </View>
                <ZenText content={CommonService.currencyStandard(currencySymbol, String(summaryItem.netSale ?? 0))} color={appColors.dark} bold size={fontSize.smallerFontSize} />
                <View style={[styles.progressBar, { backgroundColor: appColors.lightPrimary }]}>
                    <View style={[styles.progressFill, { width: '100%' }]} />
                </View>
            </TouchableOpacity>
        );
    };

    const renderOrderCard = ({ item }: { item: RevenueData }) => {
        const isSelected = isLandscape
            ? activeItem?.orderDate === item.orderDate
            : selectedItem?.orderDate === item.orderDate;
        return (
            <TouchableOpacity
                onPress={() => {
                    if (!isLandscape)
                        setModalVisible(true)
                    setSelectedItem(item)
                }}
                style={[styles.orderCard, isSelected && styles.selectedCard, { flex: 1 }]}
            >
                <View style={styles.cardHeader}>
                    <ZenText color={appColors.dark} bold content={item.orderDate} size={fontSize.smallHeaderFontSize} />
                    <ZenBadge
                        badgeColor={appColors.coolGray}
                        badgeName={`${item.orderCount} Orders`}
                        badgeTextColor={appColors.lightDark}
                        badgeTextSize={fontSize.badgeFontSize}
                        badgeTextStyle={{ fontFamily: appFonts.bold }}
                        badgeContainerStyle={{
                            paddingVertical: 2,
                            alignSelf: "center"
                        }}
                    />
                </View>
                <ZenText content={CommonService.currencyStandard(currencySymbol, String(item.netSale ?? 0))} color={appColors.dark} bold size={fontSize.smallerFontSize} />
                <View style={styles.progressBar}>
                    <View style={[styles.progressFill, { width: `${(item.netSale / summaryItem?.netSale) * 100}%` }]} />
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <View style={{ flex: 1 }}>
            <CommonHeader
                backIcon={true}
                headerName="Revenue Report"
                backIconOnpress={() => navigation.goBack()}
            />
            {loading && !offset && (!listRefresh || !value?.revenueReport) ? <ZenLoader lottie={require("../../../assets/infinityLoader.json")} lottieType='Local' /> : <View style={styles.container}>
                {/* Sidebar - List of Dates */}
                <View style={[styles.sidebar, { flex: (isLandscape && (revenueReportData?.revenueData?.length ?? 0) > 0) ? 0.4 : 1 }]}>

                    {/* NEW DATE RANGE PICKER SECTION */}
                    <View style={styles.dateFilterContainer}>
                        <ZenText
                            content="Filter Period"
                            color={appColors.lightDark}
                            size={fontSize.errorFontSize}
                            style={{ marginBottom: 8, marginLeft: 4, textTransform: 'uppercase' }}
                            bold
                        />
                        <View style={[styles.dateInputsRow]}>
                            {/* From Date */}
                            <TouchableOpacity
                                style={styles.dateBox}
                                onPress={() => setDatePickerVisible({ visibilty: true, current: 'from' })}
                            >
                                <ZenIcon type='material-community' name="calendar-range" size={18} color={appColors.blue600} />
                                <View style={{ marginLeft: 8 }}>
                                    <ZenText content="From" size={fontSize?.errorFontSize} color={appColors.lightDark} />
                                    <ZenText content={dateRange.from || "Select"} size={fontSize.tinyFontSize} bold color={appColors.dark} />
                                </View>
                            </TouchableOpacity>

                            <View style={styles.dateDivider} />

                            {/* To Date */}
                            <TouchableOpacity
                                style={styles.dateBox}
                                onPress={() => setDatePickerVisible({ visibilty: true, current: 'to' })}
                            >
                                <ZenIcon type='material-community' name="calendar-check" size={18} color={appColors.blue600} />
                                <View style={{ marginLeft: 8 }}>
                                    <ZenText content="To" size={fontSize?.errorFontSize} color={appColors.lightDark} />
                                    <ZenText content={dateRange.to || "Select"} size={fontSize.tinyFontSize} bold color={appColors.dark} />
                                </View>
                            </TouchableOpacity>
                        </View>
                    </View>

                    {(revenueReportData?.revenueData?.length ?? 0) > 0 && renderSummaryCard()}

                    <FlatList
                        data={revenueReportData?.revenueData || []}
                        refreshControl={<RefreshControl refreshing={listRefresh} onRefresh={onRefresh} />}
                        renderItem={renderOrderCard}
                        keyExtractor={(item, index) => index.toString()}
                        showsVerticalScrollIndicator={false}
                        onEndReached={() => {
                            if (!isReached && !loading) {
                                offset = offset + limit;
                                getAllRevenueReportData(offset);
                            }
                        }}
                        key={isLandscape ? '1' : '2'}
                        numColumns={isLandscape ? 1 : 2}
                        onEndReachedThreshold={0.5}
                        columnWrapperStyle={!isLandscape && (revenueReportData?.revenueData?.length ?? 0) > 0 ? { gap: 15 } : undefined}
                        ListFooterComponentStyle={{ height: revenueReportData?.revenueData?.length ? 20 : undefined }}
                        ListFooterComponent={!isReached && (revenueReportData?.revenueData?.length ?? 0) > 0 ? <View style={{ flex: 1, justifyContent: "center", alignItems: "center", height: 50 }}>
                            <ZenLoader size={"small"} color={appColors?.primary} />
                        </View> : null}
                        ListEmptyComponent={<ZenEmptyComponent />}
                        contentContainerStyle={{
                            paddingHorizontal: 15,
                            paddingBottom: (revenueReportData?.revenueData?.length ?? 0) > 0 ? 10 : 20,
                            flexGrow: (revenueReportData?.revenueData?.length ?? 0) > 0 ? undefined : 1,
                            gap: (revenueReportData?.revenueData?.length ?? 0) > 0 ? 15 : undefined,
                        }}
                    />
                </View>

                {/* Main Content - Detailed 13-Key View */}
                {isLandscape && (revenueReportData?.revenueData?.length ?? 0) > 0 && <View style={{ flex: 0.6 }}>
                    {activeItem && <RenderDetailedView selectedItem={activeItem} format={format} currencySymbol={currencySymbol!} />}
                </View>}
            </View>}

            {
                datePickerVisibile?.visibilty && <ZenDateTimePicker
                    date={datePickerVisibile?.current === 'from' ? new Date(dateRange?.from) : new Date(dateRange?.to)}
                    mode="date"
                    open={datePickerVisibile?.visibilty}
                    maximumDate={
                        datePickerVisibile?.current == 'from' && dateRange?.to
                            ? new Date(dateRange?.to)
                            : new Date()
                    }
                    minimumDate={
                        datePickerVisibile?.current !== 'from' && dateRange?.from
                            ? new Date(dateRange?.from)
                            : undefined
                    }
                    onConfirm={(data) => {
                        setDatePickerVisible({
                            current: null,
                            visibilty: false
                        });

                        if (datePickerVisibile?.current == 'from') {
                            setDateRange(prev => {
                                return {
                                    ...prev,
                                    from: moment(data).format('YYYY-MM-DD')
                                }
                            })
                            // setIsLoading(true)
                        }
                        else {
                            setDateRange(prev => {
                                return {
                                    ...prev,
                                    to: moment(data).format('YYYY-MM-DD')
                                }
                            })
                            // setIsLoading(true)
                        }
                    }}
                    onCancel={() => {
                        setDatePickerVisible({ visibilty: false, current: null });
                    }}
                />
            }
            {/* Mobile Modal */}
            <SellscreenModal isModalOpen={modalVisible && !isLandscape} setModalOpen={setModalVisible} onCloseRequest={onRequestClose} viewStyle={{ padding: 0 }} >
                <>
                    <View style={{
                        width: "100%",
                        padding: 15,
                        backgroundColor: appColors?.lightBackgroundColor,
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 10,
                    }}>
                        <ZenText content={"Revenue Report"} bold={true} size={width > 600 ? fontSize?.mediumFontSize : fontSize?.smallerFontSize} color={appColors?.dark} />
                        <TouchableOpacity onPress={onRequestClose} >
                            <ZenIcon name='close-circle' type='material-community' size={width > 600 ? 24 : 22} color={appColors?.iconColor} />
                        </TouchableOpacity>
                    </View>
                    <View style={{ height: height * 0.7 }}>
                        <RenderDetailedView selectedItem={activeItem!} format={format} currencySymbol={currencySymbol!} />
                    </View>
                </>
            </SellscreenModal>
        </View>
    )
}

export default RevenueReport

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: appColors.lightBackgroundColor, flexDirection: 'row' },
    sidebar: { borderRightWidth: 1, borderRightColor: appColors.coolGray, },
    orderCard: { padding: 16, borderRadius: 16, backgroundColor: appColors.readDisableSwitch, borderWidth: 1, borderColor: 'transparent' },
    selectedCard: { backgroundColor: appColors.light, borderColor: appColors.blue600, elevation: 4 },
    cardHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
    progressBar: { height: 4, backgroundColor: appColors.badgeInActive, borderRadius: 2, marginTop: 10 },
    progressFill: { height: 4, backgroundColor: appColors.blue600, borderRadius: 2 },

    mainContent: { flex: 1, paddingVertical: 10, paddingHorizontal: 15 },
    mainHeader: { marginBottom: 30 },

    detailGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', gap: 15 },
    tile: { width: '31%', backgroundColor: appColors.light, padding: 24, borderRadius: 24, elevation: 2 },
    tileLabel: { marginTop: 12 },
    tileValue: { marginTop: 4 },

    tilePortrait: {
        width: '100%',
        padding: 12,
        borderRadius: 16,
    },
    tilePortraitContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    iconContainerPortrait: {
        width: 44,
        height: 44,
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
    },
    tileTextContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    tileLabelPortrait: {
        marginBottom: 2,
    },
    portraitList: {
        width: '100%',
        paddingHorizontal: 5,
    },
    infoItemRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: appColors.readDisableSwitch,
    },
    infoLabelRow: {
        textTransform: 'uppercase',
    },

    infoBlock: { width: '100%', backgroundColor: appColors.light, padding: 10, borderRadius: 15, gap: 5 },
    blockTitle: { marginBottom: 5 },
    row: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
    infoItem: { flex: 1 },
    infoLabel: { textTransform: 'uppercase', marginBottom: 6 },
    dateFilterContainer: {
        paddingHorizontal: 15,
        marginBottom: 10,
        marginTop: 20
    },
    dateInputsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: appColors.lightPrimary,
        borderRadius: 12,
        padding: 8,
        borderWidth: 1,
        borderColor: appColors.lightPrimary,
    },
    dateBox: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    dateDivider: {
        width: 1,
        height: '70%',
        backgroundColor: appColors.coolGray,
        marginHorizontal: 4,
    }
});